import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import JsonEditor from 'features/prompts/components/JsonEditor';

/**
 * Output format — what shape the model should answer in.
 *
 * Three choices, which are the three states `response_format` can hold:
 *
 *   Text        → null                  no constraint
 *   JSON        → {type:'json_object'}  valid JSON, any shape
 *   Structured  → {type:'json_schema'}  exactly this schema
 *
 * The JSON / Structured split is NOT "hint vs enforced" — `json_object`
 * constrains decoding so the reply is always parseable. What it doesn't pin is
 * the SHAPE. Structured is the one that guarantees fields.
 *
 * Text is stored as null rather than {type:'text'}, because null already means
 * "no constraint" everywhere downstream: compile() emits the same thing either
 * way, every prompt written before this picker existed reads back as Text, and
 * the published SDK's ResponseFormat union stays as-is.
 *
 * Split into a menu and an editor because only ONE of the three needs a body:
 * picking Text or JSON is the whole decision, so the menu just closes. The
 * editor opens for Structured alone.
 */

// A schema that satisfies OpenAI strict mode from the first save: every
// property listed in `required`, and additionalProperties explicitly false.
// Handing over an empty `{}` would look inviting and then be rejected.
const STARTER = {
  name: 'result',
  strict: true,
  schema: {
    type: 'object',
    properties: { answer: { type: 'string' } },
    required: ['answer'],
    additionalProperties: false,
  },
};

const KINDS = ['text', 'json', 'structured'];
const LABEL = { text: 'Text', json: 'JSON', structured: 'Structured' };

/** Which of the three a stored response_format represents. */
export function kindOf(rf) {
  if (rf && rf.type === 'json_schema') return 'structured';
  if (rf && rf.type === 'json_object') return 'json';
  return 'text';
}

/** The response_format value for a chosen kind, reusing an existing schema. */
function valueFor(kind, current) {
  if (kind === 'text') return null;
  if (kind === 'json') return { type: 'json_object' };
  const existing = current && current.type === 'json_schema' ? current.json_schema : null;
  return { type: 'json_schema', json_schema: existing || STARTER };
}

/**
 * The toolbar control. Matches the neighbouring section buttons, but opens a
 * menu instead of a panel — `onPick` tells the parent whether a body is needed.
 */
export function OutputFormatMenu({ value, onChange, onPick, disabled, active }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const kind = kindOf(value);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const choose = (k) => {
    setOpen(false);
    if (k !== kind) onChange(valueFor(k, value));
    onPick?.(k);
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--border-default)] bg-surface px-2.5 text-[12px] font-medium text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover disabled:opacity-50 ${
          active ? 'border-[color:var(--border-strong)] bg-surface-hover' : ''
        }`}
      >
        Output format
        <span className="text-[11px] font-normal text-[color:var(--text-muted)]">{LABEL[kind]}</span>
        <ChevronDown size={13} className={`transition-transform ${open ? 'rotate-180' : ''} text-[color:var(--text-muted)]`} />
      </button>
      {open && (
        <div className="absolute left-0 top-[calc(100%+4px)] z-30 min-w-[150px] overflow-hidden rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] py-1 shadow-lg">
          {KINDS.map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => choose(k)}
              className={`flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-[12.5px] text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--surface-hover)] ${
                k === kind ? 'bg-[color:var(--surface-hover)]' : ''
              }`}
            >
              <span className="min-w-0 flex-1 truncate">{LABEL[k]}</span>
              {k === kind && <Check className="h-3.5 w-3.5 shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Segmented picker plus the schema body — for places with no toolbar to hang a
 * menu off, like the playground's Output modal. Same three states, same
 * rule: only Structured shows an editor.
 */
export function OutputFormatChoice({ value, onChange, disabled }) {
  const kind = kindOf(value);
  const btnCls = (k) => `inline-flex h-7 items-center rounded-md px-2.5 text-[12px] font-medium transition-colors ${
    kind === k
      ? 'bg-surface-hover text-[color:var(--text-primary)]'
      : 'text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]'
  }`;
  return (
    <div className="flex flex-col gap-3">
      <div className="inline-flex w-fit items-center gap-0.5">
        {KINDS.map((k) => (
          <button
            key={k}
            type="button"
            disabled={disabled}
            onClick={() => { if (k !== kind) onChange(valueFor(k, value)); }}
            className={btnCls(k)}
          >
            {LABEL[k]}
          </button>
        ))}
      </div>
      {kind === 'structured' && (
        <OutputSchemaEditor value={value} onChange={onChange} disabled={disabled} />
      )}
    </div>
  );
}

/** The schema body — only ever rendered for Structured. */
export function OutputSchemaEditor({ value, onChange, disabled }) {
  return (
    <JsonEditor
      value={value?.json_schema ?? STARTER}
      emptyValue={STARTER}
      disabled={disabled}
      onChange={(json_schema) => onChange({ type: 'json_schema', json_schema })}
    />
  );
}
